import { Sprite } from "pixi.js";
import { getComponent, type Entity } from "../world/Entity";
import type { AnimationTarget } from "../animation/animations";
import type { AssetLoader } from "../assets/AssetLoader";

const PLACEHOLDER_TINT = 0xff66cc;

/** One-directional: reads `Entity` state and syncs a Pixi sprite. Never writes back into the entity.
 *  Re-resolves its texture from `assets` every sync (like `CharacterView`'s layers) so a definitionId/
 *  asset change — e.g. artifact discovery swapping the stone for amber — shows up without recreating the view. */
export class EntityView implements AnimationTarget {
  readonly sprite: Sprite;

  constructor(
    entity: Entity,
    private readonly assets: AssetLoader,
  ) {
    const renderable = getComponent(entity, "renderable");
    const key = renderable?.asset ?? entity.definitionId;
    const { texture } = this.assets.textureFor(key);
    this.sprite = new Sprite({ texture, anchor: { x: 0.5, y: 1 } });
    this.sync(entity);
  }

  sync(entity: Entity): void {
    this.sprite.position.set(entity.transform.position.x, entity.transform.position.y);
    this.sprite.rotation = entity.transform.rotation;
    this.sprite.visible = entity.visible;
    const renderable = getComponent(entity, "renderable");
    this.sprite.zIndex = renderable?.zIndex ?? entity.transform.position.y;

    const key = renderable?.asset ?? entity.definitionId;
    const { texture, isPlaceholder } = this.assets.textureFor(key);
    this.sprite.texture = texture;
    this.sprite.tint = isPlaceholder ? PLACEHOLDER_TINT : 0xffffff;
    if (isPlaceholder) this.assets.loadKeys([key]);
  }

  destroy(): void {
    this.sprite.destroy();
  }

  get x(): number {
    return this.sprite.x;
  }
  set x(v: number) {
    this.sprite.x = v;
  }
  get y(): number {
    return this.sprite.y;
  }
  set y(v: number) {
    this.sprite.y = v;
  }
  get scaleX(): number {
    return this.sprite.scale.x;
  }
  set scaleX(v: number) {
    this.sprite.scale.x = v;
  }
  get scaleY(): number {
    return this.sprite.scale.y;
  }
  set scaleY(v: number) {
    this.sprite.scale.y = v;
  }
  get rotation(): number {
    return this.sprite.rotation;
  }
  set rotation(v: number) {
    this.sprite.rotation = v;
  }
  get alpha(): number {
    return this.sprite.alpha;
  }
  set alpha(v: number) {
    this.sprite.alpha = v;
  }
}
